import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { CampoDeSenha } from '@/components/CampoDeSenha/CampoDeSenha';
import { Logo } from '@/components/Logo/Logo';
import { obterInstrucaoDeRecuperacao } from '@/features/autenticacao/api/autenticacaoApi';
import { useSessao } from '@/features/autenticacao/useSessao';
import { NOME_DO_PRODUTO } from '@/lib/produto';
import { descreverErro } from '@/lib/erros';

import estilos from './PaginaDeLogin.module.css';

/**
 * Tela de login. Especificação V3.1, item 15.1.
 *
 * A validação aqui é só de campo vazio e formato de e-mail. Não há verificação de
 * tamanho de senha: a política de 15 caracteres vale para DEFINIR senha, e aplicar a
 * mesma regra no login recusaria localmente quem tem senha antiga mais curta, sem nem
 * consultar o servidor.
 */

interface CampoDeLogin {
  email: string;
  senha: string;
}

interface EstadoDaRota {
  de?: string;
}

export function PaginaDeLogin() {
  const { autenticado, entrar, restaurando } = useSessao();
  const navegar = useNavigate();
  const local = useLocation();

  const [falha, definirFalha] = useState<string | null>(null);
  const [instrucao, definirInstrucao] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CampoDeLogin>({ defaultValues: { email: '', senha: '' } });

  const destino = (local.state as EstadoDaRota | null)?.de ?? '/';

  /*
    Enquanto a restauração corre, não desenha o formulário. Quem abre /login direto com
    sessão válida no cookie veria o formulário por um instante e seria levado embora no
    quadro seguinte — e quem começou a digitar perderia o que digitou.
  */
  if (restaurando) {
    return (
      <main className={estilos.tela}>
        <p className={estilos.assinatura} role="status">
          Verificando sua sessão...
        </p>
      </main>
    );
  }

  // Quem já está autenticado não tem o que fazer aqui. Acontece ao usar o botão voltar
  // do navegador depois de entrar.
  if (autenticado) {
    return <Navigate to={destino} replace />;
  }

  const enviar = handleSubmit(async ({ email, senha }) => {
    definirFalha(null);

    try {
      await entrar(email, senha);

      void navegar(destino, { replace: true });
    } catch (erro) {
      // A mensagem vem do servidor e é a mesma para senha errada, conta inexistente,
      // conta bloqueada e conta desativada. Não tentamos adivinhar qual foi:
      // diferenciar aqui reintroduziria, na interface, o vazamento que o servidor
      // evita de propósito.
      definirFalha(descreverErro(erro));
    }
  });

  const mostrarRecuperacao = async () => {
    try {
      const resposta = await obterInstrucaoDeRecuperacao();

      definirInstrucao(resposta.instrucao);
    } catch (erro) {
      definirInstrucao(descreverErro(erro));
    }
  };

  return (
    <main className={estilos.tela}>
      <div>
        <div className={estilos.cartao}>
          <div className={estilos.marca}>
            <Logo variante="positiva" altura={32} textoAlternativo="FAP Online" />
          </div>

          <h1 className={estilos.titulo}>{NOME_DO_PRODUTO}</h1>
          <p className={estilos.apoio}>Entre com seu e-mail corporativo.</p>

          {/* role="alert" para o leitor de tela anunciar a falha sem que o usuário
              precise navegar até ela. */}
          {falha !== null && (
            <p className={estilos.aviso} role="alert">
              {falha}
            </p>
          )}

          <form
            onSubmit={(evento) => {
              void enviar(evento);
            }}
            noValidate
          >
            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="email">
                E-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                className={
                  errors.email ? `${estilos.entrada} ${estilos.entradaInvalida}` : estilos.entrada
                }
                aria-invalid={errors.email !== undefined}
                aria-describedby={errors.email === undefined ? undefined : 'erro-email'}
                {...register('email', {
                  required: 'Informe seu e-mail.',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido.' },
                })}
              />
              {errors.email !== undefined && (
                <span className={estilos.erroDeCampo} id="erro-email">
                  {errors.email.message}
                </span>
              )}
            </div>

            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="senha">
                Senha
              </label>
              {/*
                O olho permite ver o que está no campo. Necessário na prática: o
                preenchimento automático do navegador já colocou o e-mail aqui, e o
                login falhava com "e-mail ou senha incorretos" sem que houvesse como
                perceber que o campo estava com outro conteúdo.
              */}
              <CampoDeSenha
                id="senha"
                autoComplete="current-password"
                invalido={errors.senha !== undefined}
                aria-invalid={errors.senha !== undefined}
                aria-describedby={errors.senha === undefined ? undefined : 'erro-senha'}
                {...register('senha', { required: 'Informe sua senha.' })}
              />
              {errors.senha !== undefined && (
                <span className={estilos.erroDeCampo} id="erro-senha">
                  {errors.senha.message}
                </span>
              )}
            </div>

            <button className={estilos.botao} type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          {/* Decisão 12: a v1 não envia e-mail. O texto vem do servidor, para que a
              instrução mude sem novo build quando existir serviço de e-mail. */}
          <p className={estilos.rodape}>
            {instrucao === null ? (
              <button
                type="button"
                className={estilos.acessorio}
                onClick={() => {
                  void mostrarRecuperacao();
                }}
              >
                Esqueci minha senha
              </button>
            ) : (
              instrucao
            )}
          </p>
        </div>

        <p className={estilos.assinatura}>FAP Online</p>
      </div>
    </main>
  );
}
